/**
 * 任务域 hooks（TanStack Query）：领域/项目/标签/任务/习惯/专注/统计。
 * 失效策略：任务写操作 → tasks + task + stats；习惯/专注 → 对应列表 + stats。
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  addReminder,
  addSubtask,
  attachTag,
  cancelTask,
  completeProject,
  completeTask,
  createArea,
  createHabit,
  createProject,
  createTask,
  deleteArea,
  deleteHabit,
  deleteProject,
  deleteReminder,
  deleteSubtask,
  deleteTask,
  detachTag,
  endFocus,
  getStats,
  getTask,
  listAreas,
  listFocus,
  listHabitLogs,
  listHabits,
  listProjects,
  listTags,
  listTasks,
  startFocus,
  updateArea,
  updateHabit,
  updateProject,
  updateSubtask,
  updateTask,
  upsertHabitLog,
} from '../lib/api';
import type {
  CreateAreaBody,
  CreateFocusBody,
  CreateHabitBody,
  CreateHabitLogBody,
  CreateProjectBody,
  CreateTaskBody,
  EndFocusBody,
  TasksQueryParams,
  UpdateAreaBody,
  UpdateHabitBody,
  UpdateProjectBody,
  UpdateTaskBody,
} from '../lib/types';

function invalidateTaskData(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: ['tasks'] });
  qc.invalidateQueries({ queryKey: ['task'] });
  qc.invalidateQueries({ queryKey: ['stats'] });
}

// ==================== 领域 ====================

export function useAreas(archived?: string) {
  return useQuery({
    queryKey: ['areas', archived ?? 'active'],
    queryFn: () => listAreas(archived ? { archived } : undefined),
  });
}

export function useCreateArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAreaBody) => createArea(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['areas'] }),
  });
}

export function useUpdateArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateAreaBody }) => updateArea(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['areas'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useDeleteArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteArea(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['areas'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      invalidateTaskData(qc);
    },
  });
}

// ==================== 项目 ====================

export function useProjects(query?: {
  status?: string;
  areaId?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: ['projects', query ?? {}],
    queryFn: () => listProjects(query),
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProjectBody) => createProject(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateProjectBody }) => updateProject(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useCompleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => completeProject(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteProject(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      invalidateTaskData(qc);
    },
  });
}

// ==================== 标签 ====================

export function useTags() {
  return useQuery({ queryKey: ['tags'], queryFn: () => listTags() });
}

// ==================== 任务 ====================

export function useTasks(query?: TasksQueryParams) {
  return useQuery({
    queryKey: ['tasks', query ?? {}],
    queryFn: () => listTasks(query),
  });
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: ['task', id],
    queryFn: () => getTask(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTaskBody) => createTask(body),
    onSuccess: () => invalidateTaskData(qc),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateTaskBody }) => updateTask(id, body),
    onSuccess: () => invalidateTaskData(qc),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTask(id),
    onSuccess: () => invalidateTaskData(qc),
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => completeTask(id),
    onSuccess: () => invalidateTaskData(qc),
  });
}

export function useCancelTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelTask(id),
    onSuccess: () => invalidateTaskData(qc),
  });
}

// ==================== 子任务 / 标签 / 提醒 ====================

export function useAddSubtask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, title }: { taskId: string; title: string }) =>
      addSubtask(taskId, title),
    onSuccess: () => invalidateTaskData(qc),
  });
}

export function useUpdateSubtask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      taskId,
      sid,
      body,
    }: {
      taskId: string;
      sid: string;
      body: { title?: string; done?: boolean };
    }) => updateSubtask(taskId, sid, body),
    onSuccess: () => invalidateTaskData(qc),
  });
}

export function useDeleteSubtask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, sid }: { taskId: string; sid: string }) => deleteSubtask(taskId, sid),
    onSuccess: () => invalidateTaskData(qc),
  });
}

export function useAttachTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, name }: { taskId: string; name: string }) => attachTag(taskId, name),
    onSuccess: () => {
      invalidateTaskData(qc);
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export function useDetachTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, name }: { taskId: string; name: string }) => detachTag(taskId, name),
    onSuccess: () => {
      invalidateTaskData(qc);
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export function useAddReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      taskId,
      remindAt,
      channel,
    }: {
      taskId: string;
      remindAt: string;
      channel: 'INAPP' | 'WEBHOOK' | 'EMAIL';
    }) => addReminder(taskId, { remindAt, channel }),
    onSuccess: () => invalidateTaskData(qc),
  });
}

export function useDeleteReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, rid }: { taskId: string; rid: string }) => deleteReminder(taskId, rid),
    onSuccess: () => invalidateTaskData(qc),
  });
}

// ==================== 习惯 ====================

export function useHabits(archived?: string) {
  return useQuery({
    queryKey: ['habits', archived ?? 'active'],
    queryFn: () => listHabits(archived ? { archived } : undefined),
  });
}

export function useHabitLogs(habitId: string | undefined, from?: string, to?: string) {
  return useQuery({
    queryKey: ['habitLogs', habitId, from ?? '', to ?? ''],
    queryFn: () => listHabitLogs(habitId as string, { from, to }),
    enabled: Boolean(habitId),
  });
}

export function useCreateHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateHabitBody) => createHabit(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['habits'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

export function useUpdateHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateHabitBody }) => updateHabit(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['habits'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

export function useDeleteHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteHabit(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['habits'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

export function useUpsertHabitLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ habitId, body }: { habitId: string; body: CreateHabitLogBody }) =>
      upsertHabitLog(habitId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['habits'] });
      qc.invalidateQueries({ queryKey: ['habitLogs'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

// ==================== 专注 ====================

export function useFocusSessions(query?: {
  from?: string;
  to?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: ['focus', query ?? {}],
    queryFn: () => listFocus(query),
  });
}

export function useStartFocus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFocusBody) => startFocus(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['focus'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

export function useEndFocus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: EndFocusBody }) => endFocus(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['focus'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

// ==================== 统计 ====================

export function useStats(from?: string, to?: string) {
  return useQuery({
    queryKey: ['stats', from ?? '', to ?? ''],
    queryFn: () => getStats({ from, to }),
  });
}
